import AlertPopup from '../ui/AlertPopup';
import Disaster from './Disaster';
import common from '../common';

export default class Wildfire extends Disaster {
	constructor(world) {
		super(world);

		this._canUpdate = true;

		this._trees = [];
		this._fireTiles = [];

		this.igniteCountdownLast = Date.now();
		this._treeFireCount = 0;
		this._fireHasSpread = false;

		this.igniteCountdown = 500; // Speed of spread check
		this._fireLengthUpper = 15000; // extinguish countdown ranges
		this._fireLengthLower = 10000;
		this._igniteChance = 0.7; // normalised to 0-1 (0.3 === 30%)

		for (let y = 0, rows = this._world.rows; y < rows; ++y) {
			for (let x = 0, columns = this._world.columns; x < columns; ++x) {
				this.initTile(this._world.getTile(x, y));
			}
		}
		this._ambientSound = P3.audio.playSound('sfx_wf_ambience_loop_00', { loop: true });

		this._fireSound = null;
		this._destroyed = false;
	}

	stopSounds() {
		super.stopSounds();

		if (this._fireSound) {
			this._fireSound.stop();
			P3.audio.cleanupAudio(this._fireSound);
		}
		this._fireSound = null;
	}

	destroy() {
		this._canUpdate = false;
		this._destroyed = true;

		super.destroy();
	}

	update(dt) {
		super.update(dt);

		if (!this._isRunning || !this._canUpdate) {
			return;
		}

		// only end if the fire has spread enough and there aren't many trees left
		if (this._fireHasSpread && this._treeFireCount <= 30) {
			this.end();
		} else if (!this._fireHasSpread && this._treeFireCount > 30) {
			this._fireHasSpread = true;
		}

		if (Date.now() - this.igniteCountdownLast > this.igniteCountdown) {
			this.igniteCountdownLast = Date.now();

			const size = this._fireTiles.length;

			for (let i = 0; i < size; i++) {
				if (!this._fireTiles[i].wildfire.isFire && !this._fireTiles[i].wildfire.ignitable) {
					continue;
				}

				for (const ignitable of this._fireTiles[i].wildfire.canIgnite) {
					if (Math.random() > 1 - this._igniteChance) {
						this.igniteStructure(ignitable);
					}
				}
			}
		}
		const size = this._fireTiles.length;

		for (let i = 0; i < size; i++) {
			if (this._fireTiles[i].wildfire.isFire && !this._fireTiles[i].wildfire.ignitable) {
				if (Date.now() - this._fireTiles[i].wildfire.igniteTime > P3.math.randomInt(this._fireLengthLower, this._fireLengthUpper)) {
					new TWEEN.Tween(this._fireTiles[i]._fireAnim, this._tweens)
						.to({
							alpha: 0,
						}, 1000)
						.easing(TWEEN.Easing.Linear.None)
						.delay(500)
						.onStart(() => {
							this._fireTiles[i].wildfire.isFire = false;
						})
						.onComplete(() => {
							if (this._fireTiles[i]._fireAnim) {
								this._fireTiles[i]._fireAnim.stop();
								this._fireTiles[i]._fireAnim.visible = false;
								this._fireTiles[i]._fireAnim.destroy();
								this._fireTiles[i]._fireAnim = null;
							}

							this._fireTiles[i].wildfire.ignitable = false;

							const strength = this._fireTiles[i].strength;

							if (Math.random() > strength / 100) {
								this.destroyStructure(this._fireTiles[i]);
							}
						}, this)
						.start();
				}
			}
		}
	}

	charGround(tile) {
		const c = [
			0x7D6856,
			0x54493F,
			0x373737,
		];

		tile.groundSprite.tint = P3.utils.selectRandom(c);
	}

	destroyStructure(tile) {
		if (tile.damaged) {
			return;
		}
		tile.damaged = true;
		tile.setStructureTexture();
		this.charGround(tile);
		this._treeFireCount--;

		const structure = tile.data.structure;

		if (structure.population > 0) {
			const type = AlertPopup.TYPE_DEAD;
			const cost = structure.cost * 0.5;
			const survive = 0;
			let dead = structure.population * 0.5;
			const injured = Math.max(0, structure.population - dead);

			dead = Math.random() > 0.5 ? Math.ceil(dead) : Math.floor(dead);
			this._totalDestroyed++;

			this._hospitalDestroyed = structure.special && structure.special === 'hospital';
			this._schoolDestroyed = structure.special && structure.special === 'school';

			tile.parent && tile.alert.start(type);

			this._totalDeaths += dead;
			this._totalInjury += injured;
			this._totalSurvive += survive;
			this._totalCost += cost;
		}

		if (this._treeFireCount <= 30) {
			this.end();
		}
	}

	initTile(tile) {
		if (tile.data.structure && [2, 21].includes(tile.data.structure.group)) {
			this._trees.push(tile);
		}

		tile.wildfire = {};
		tile.wildfire.isFire = false;
		tile.wildfire.igniteTime = 0;
		tile.wildfire.ignitable = true;
	}

	checkIgnitable(tile) {
		tile.wildfire.canIgnite = [];

		if (tile.up && tile.up.data.structure) {
			if (tile.up.data.structure.ignitable) {
				tile.wildfire.canIgnite.push(tile.up);
			}
		}

		if (tile.down && tile.down.data.structure) {
			if (tile.down.data.structure.ignitable) {
				tile.wildfire.canIgnite.push(tile.down);
			}
		}

		if (tile.left && tile.left.data.structure) {
			if (tile.left.data.structure.ignitable) {
				tile.wildfire.canIgnite.push(tile.left);
			}
		}

		if (tile.right && tile.right.data.structure) {
			if (tile.right.data.structure.ignitable) {
				tile.wildfire.canIgnite.push(tile.right);
			}
		}
	}

	start() {
		if (super.start()) {
			P3.audio.playSound('sfx_wf_disaster_strikes_start_00', {
				callback: () => {
					this._fireSound = P3.audio.playSound('sfx_wf_disaster_strikes_loop_00', { loop: true });
				},
			});
			for (let y = 0, rows = this._world.rows; y < rows; ++y) {
				for (let x = 0, columns = this._world.columns; x < columns; ++x) {
					this.checkIgnitable(this._world.getTile(x, y));
				}
			}
			if (this._world.isWildfire) {
				return;
			}
			this._world.isWildfire = true;
			this.igniteStructure(this._trees[P3.utils.selectRandom([100, 200, 300, 400, 500])]);
		}
	}

	end() {
		if (!this._isRunning) {
			return;
		}

		this.stopSounds();

		P3.audio.playSound('sfx_wf_disaster_strikes_end_00');

		const unhoused = common.scenario.population.unhoused;

		this._totalDeaths += P3.math.randomInt(0, unhoused); // kills a random amount of unhoused civs
		this._totalDeaths *= Math.max(1 - (common.scenario._ccUpgrades.length / 10), 0.5);
		this._totalDeaths = Math.floor(this._totalDeaths);
		this._totalSurvive = common.scenario.population.total - this._totalDeaths;

		if (!this._destroyed) {
			this.signals.disasterEnd.dispatch(this);
		}
		this._isRunning = false;
		this._canStart = false;
	}

	igniteStructure(tile) {
		if (tile.wildfire.isFire || !tile.wildfire.ignitable) {
			return;
		}
		tile.wildfire.isFire = true;
		tile.wildfire.igniteTime = Date.now();
		tile.wildfire.ignitable = false;
		this._treeFireCount++;

		this._fireTiles.push(tile);

		tile.playFireAnim();
	}
}
